import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TaskCard } from './task-card';
import { cn } from '@/lib/utils';
import { memo } from 'react';

interface KanbanColumnProps {
  id: string;
  title: string;
  tasks: any[];
  color: string;
  onTaskClick: (task: any) => void;
  onTaskDelete?: (task: any) => void;
  highlightedTaskId?: string | null;
}

export const KanbanColumn = memo(function KanbanColumn({ id, title, tasks, color, onTaskClick, onTaskDelete, highlightedTaskId }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div className="flex flex-col flex-shrink-0 w-72">
      {/* Column header */}
      <div className={cn(
        'flex items-center justify-between px-3 py-2 mb-3 rounded-lg border',
        'bg-[var(--bg-secondary)]/50 backdrop-blur-sm',
      )}>
        <div className="flex items-center gap-2">
          <div className={cn('w-2.5 h-2.5 rounded-full', color)} />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
        </div>
        <span className="text-xs font-medium text-[var(--text-secondary)] bg-[var(--bg-primary)] px-2 py-0.5 rounded-full">
          {tasks.length}
        </span>
      </div>

      {/* Droppable area */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex flex-col gap-2 p-2 rounded-lg min-h-[200px] transition-colors duration-200',
          'overflow-y-auto max-h-[calc(100vh-250px)]',
          isOver && 'bg-dyndev-green/5 border-2 border-dashed border-dyndev-green/30',
          tasks.length === 0 && !isOver && 'border-2 border-dashed border-[var(--border-color)]'
        )}
      >
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => onTaskClick(task)}
              onDelete={onTaskDelete}
              highlighted={task.id === highlightedTaskId}
            />
          ))}
        </SortableContext>

        {tasks.length === 0 && !isOver && (
          <div className="flex items-center justify-center h-20 text-xs text-[var(--text-secondary)]">
            Drop tasks here
          </div>
        )}
      </div>
    </div>
  );
});
